import { Col, Row, Skeleton, Typography } from 'antd';
import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { User } from 'services/types/ProfileTypes';
import FollowItem from '../Follow/FollowItem';

const { Title } = Typography;

interface Props {
  title?: string;
  isLoading: boolean;
  users: User[];
  count?: number;
  closeHandler?: () => void;
  nextPageHandler?: () => void;
  followHandler?: (user: User) => Promise<void>;
}

const UserList: React.FC<Props> = ({
  title,
  isLoading,
  users,
  count,
  closeHandler,
  nextPageHandler,
  followHandler,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const observerRef = useRef(null);

  useEffect(() => {
    let observerRefValue: Element | null = null;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          nextPageHandler?.();
        }
      },
      { threshold: 1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
      observerRefValue = observerRef.current;
    }

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue);
      }
    };
  }, [observerRef, nextPageHandler]);

  // Handler for user click: navigate and close
  const handleUserClick = useCallback((user: User) => {
    if (user?.id) {
      navigate(`/profile/${user.id}`);
      closeHandler?.();
    }
  }, [navigate, closeHandler]);

  return (
    <div>
      {isLoading
        ? <Skeleton title={{ width: 100 }} paragraph={false} active />
        : !!count
          ? <Title level={5} style={{ margin: '12px 0 20px' }}>
            {t('search.users_count', { count })}
          </Title>
          : !!title &&
          <Title level={5} style={{ margin: '12px 0 20px' }}>{title}</Title>}
      <Row gutter={[32, 16]}>
        {users.map((item, i) => (
          <Col key={i} span={24}>
            <FollowItem
              user={item}
              followHandler={followHandler}
              onUserClick={handleUserClick}
            />
          </Col>
        ))}
        {isLoading && <>
          <Col span={24}><FollowItem /></Col>
          <Col span={24}><FollowItem /></Col>
          <Col span={24}><FollowItem /></Col>
        </>}
      </Row>
      {!isLoading && users.length > 0 && <div ref={observerRef}></div>}
    </div>
  );
}

export default UserList;